import type {Extension, CreateExtArgs, ExtensionAPI, DalItem, DmApis} from '@wix/document-manager-core'
import type {CompRef, ContextsObject} from '@wix/document-services-types'
import {ReportableError} from '@wix/document-manager-utils'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {DataExtensionAPI} from '../data'
import {DATA_TYPES} from '../../constants/constants'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import _ from 'lodash'

type ContextsExtensionAPI = ExtensionAPI & {
    contexts: ContextsObject
}

const throwContextsError = (method: string, message: string): never => {
    throw new ReportableError({errorType: 'ContextsError', message: `contexts.${method}: ${message}`})
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, coreConfig, pointers}: CreateExtArgs): ContextsExtensionAPI => {
        const {dataModel, data: dataExtension} = extensionAPI as DataModelExtensionAPI & DataExtensionAPI
        const isHardcodedContextsSchemasExpOn = coreConfig.experimentInstance.isOpen(
            'dm_enableHardcodedContextsSchemas'
        )

        const validateCompRef = (method: string, compRef: CompRef): void => {
            if (!compRef) {
                throwContextsError(
                    method,
                    'componentPointer is required. Pass the pointer of the component you want to attach the context to'
                )
            }
        }

        const validateContextType = (method: string, contextType: string): void => {
            if (!contextType) {
                throwContextsError(
                    method,
                    'contextType is required. Pass the name of the context you want to attach to the component'
                )
            }
            if (!_.isString(contextType)) {
                throwContextsError(
                    method,
                    'contextType must be a string. Pass the name of the context you want to attach to the component'
                )
            }
        }

        const validateData = (method: string, data: object): void => {
            if (!data) {
                throwContextsError(
                    method,
                    'data is required. You must pass a data object. If you want to remove all data, use contexts.data.remove'
                )
            }
            if (!_.isObject(data)) {
                throwContextsError(
                    method,
                    'data must be an object. You must pass a data object. If you want to remove all data, use contexts.data.remove'
                )
            }
        }

        const validateProperties = (method: string, properties: string[]): void => {
            if (!properties) {
                throwContextsError(method, 'properties is required.')
            }
            if (!_.isArray(properties)) {
                throwContextsError(method, 'properties must be an array')
            }
        }

        const validateComponentExists = (method: string, {id, type}: CompRef): void => {
            if (!dataModel.getItem(id, type)) {
                throwContextsError(
                    method,
                    `Component not found: ${id}. Pass a pointer of a component that exists in the document`
                )
            }
        }

        const getContextListFromCompRef = (compRef: CompRef): DalItem | undefined => {
            return dataModel.components.getItem(compRef, DATA_TYPES.contexts)
        }

        const getContextConfigFromCompRef = (
            compRef: CompRef,
            contextType: string,
            optionalContextList?: DalItem
        ): DalItem | undefined => {
            const contextList = optionalContextList ?? getContextListFromCompRef(compRef)
            return contextList?.values.find((c: DalItem) => c.contextType === contextType)
        }

        const attachContext = (compRef: CompRef, contextType: string, data?: object): void => {
            validateCompRef('attach', compRef)
            validateContextType('attach', contextType)
            validateComponentExists('attach', compRef)

            //TODO this should be removed once POC phase is over
            if (isHardcodedContextsSchemasExpOn) {
                coreConfig.schemaService?.markAsBuilder(`${contextType}.Data`)
            }

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList) {
                dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                    type: 'ContextsList',
                    values: [
                        {
                            type: 'ContextConfig',
                            contextType,
                            referredType: contextType,
                            data
                        }
                    ]
                })
                return
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (existingContext) {
                return
            }

            dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                type: 'ContextsList',
                values: [
                    ...contextList.values,
                    {
                        type: 'ContextConfig',
                        referredType: contextType,
                        contextType,
                        data
                    }
                ]
            })
        }

        const detachContext = (compRef: CompRef, contextType: string): void => {
            validateCompRef('detach', compRef)
            validateContextType('detach', contextType)
            validateComponentExists('detach', compRef)

            //TODO this should be removed once POC phase is over
            if (isHardcodedContextsSchemasExpOn) {
                coreConfig.schemaService?.markAsBuilder(`${contextType}.Data`)
            }

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                return
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!existingContext) {
                return
            }

            dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                type: 'ContextsList',
                values: contextList.values.filter((c: DalItem) => c.contextType !== contextType)
            })
        }

        const listContexts = (
            compRef: CompRef
            // will be extended to have 'options.includeAbove', default false
        ): {
            contextType: string
            target: 'self' // will be extended to have also 'above'
        }[] => {
            validateCompRef('list', compRef)
            validateComponentExists('list', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                return []
            }

            return contextList.values.map((context: DalItem) => ({
                contextType: context.contextType,
                target: 'self' // current api only return the contexts on compRef
            }))
        }

        const getData = (compRef: CompRef, contextType: string): DalItem | undefined => {
            validateCompRef('data.get', compRef)
            validateContextType('data.get', contextType)
            validateComponentExists('data.get', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                throwContextsError(
                    'data.get',
                    `No context type ${contextType} for component: ${compRef.id}, a context must exist on this component for you to get it's data`
                )
            }

            const context = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!context) {
                throwContextsError('data.get', `Context not found: ${contextType}`)
            }

            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(context?.data?._id)

            return dataExtension.getInLanguage(pageId, dataId)
        }

        const updateData = (compRef: CompRef, contextType: string, data: object): void => {
            validateCompRef('data.update', compRef)
            validateContextType('data.update', contextType)
            validateData('data.update', data)
            validateComponentExists('data.update', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                throwContextsError(
                    'data.update',
                    `No context type ${contextType} for component: ${compRef.id}, a context must exist on this component for you to update it's data`
                )
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!existingContext) {
                throwContextsError(
                    'data.update',
                    `Context not found: ${contextType}. A context must exist on this component for you to update it's data`
                )
            }

            //TODO this should be removed once POC phase is over
            if (isHardcodedContextsSchemasExpOn) {
                coreConfig.schemaService?.markAsBuilder(`${contextType}.Data`)
            }

            dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                type: 'ContextsList',
                values: contextList?.values.map((c: DalItem) => (c.contextType === contextType ? {...c, data} : c))
            })
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const removeData = (compRef: CompRef, contextType: string): void => {
            throwContextsError('data.remove', 'not implemented yet')
        }

        const removeDataProperties = (compRef: CompRef, contextType: string, properties: string[]): void => {
            validateCompRef('data.removeProperties', compRef)
            validateContextType('data.removeProperties', contextType)
            validateProperties('data.removeProperties', properties)
            validateComponentExists('data.removeProperties', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                throwContextsError('data.removeProperties', `No context for component: ${compRef.id}`)
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!existingContext) {
                throwContextsError('data.removeProperties', `Context not found: ${contextType}`)
            }

            if (isHardcodedContextsSchemasExpOn) {
                coreConfig.schemaService?.markAsBuilder(`${contextType}.Data`)
            }

            const nullifiedProperties = Object.fromEntries(properties.map(prop => [prop, null]))

            dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                type: 'ContextsList',
                values: contextList!.values.map((c: DalItem) =>
                    c.contextType === contextType ? {...c, data: nullifiedProperties} : c
                )
            })
        }

        return {
            contexts: {
                attach: attachContext,
                detach: detachContext,
                list: listContexts,
                data: {
                    get: getData,
                    update: updateData,
                    remove: removeData,
                    removeProperties: removeDataProperties
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): ContextsExtensionAPI => {
        const {contexts} = extensionAPI as ContextsExtensionAPI
        return {
            contexts: {
                attach: (compRef: CompRef, contextType: string, data?: object) =>
                    contexts.attach(compRef, contextType, data),
                detach: (compRef: CompRef, contextType: string) => contexts.detach(compRef, contextType),
                list: (compRef: CompRef) => contexts.list(compRef),
                data: {
                    get: (compRef: CompRef, contextType: string) => contexts.data.get(compRef, contextType),
                    update: (compRef: CompRef, contextType: string, data: object) =>
                        contexts.data.update(compRef, contextType, data),
                    remove: (compRef: CompRef, contextType: string) => contexts.data.remove(compRef, contextType),
                    removeProperties: (compRef: CompRef, contextType: string, properties: string[]) =>
                        contexts.data.removeProperties(compRef, contextType, properties)
                }
            }
        }
    }

    return {
        name: 'contexts',
        dependencies: new Set(['dataModel', 'structure']),
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
